import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import {useCallback, useEffect, useState} from "react";
import SoftAlert from "../../components/Alert";
import childApiClient from "../../api/children/childrenApiClient";
import SoftInput from "../../components/SoftInput";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {IconButton, Select, MenuItem, CircularProgress} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon} from "@mui/icons-material";
import AddChildrenDialog from "./components/AddChildrenDialog";
import SoftButton from "../../components/SoftButton";
import DeleteConfirmDialog from "../../components/Dialogs/DeleteConfirmDialog";
import EnhancedTable from "../../components/EnhancedTable";
import DataTable, {renderBase64ImageCell, renderPill} from "../../components/DataTable";
import { getStatusColor, getStatusDisplayName } from "../../utils/statusUtils";
import { getAllBranches } from "../../api/branch/branchApiClient";
import { getAllRoomsByBranchId } from "../../api/room/roomApiClient";

function Overview() {
    const [children, setChildren] = useState([]);
    const [filteredChildren, setFilteredChildren] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [addEditDialogOpen, setAddEditDialogOpen] = useState(false);
    const [selectedChild, setSelectedChild] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [childToDelete, setChildToDelete] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Check if we're on a child route (child details)
    const isOnChildRoute = location.pathname.includes('/children/') && location.pathname !== '/children';

    // Filter state
    const [branches, setBranches] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [selectedBranchId, setSelectedBranchId] = useState(null);
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [isLoadingBranches, setIsLoadingBranches] = useState(false);
    const [isLoadingRooms, setIsLoadingRooms] = useState(false);

    const columns = [
        {
            name: "first Name",
            value: "firstName",
            align: "left",
            width: "10",
            cell: ""
        },
        {
            name: "Last Name",
            value: "lastName",
            align: "left",
            width: "10",
            cell: ""
        },
        {
            name: "dob",
            value: "dob",
            align: "left",
            width: "10",
            cell: ""
        },
        {name: "first Name", value: "firstName", align: "left", width: "10"},
        {name: "Last Name", value: "lastName", align: "left", width: "10"},
        {name: "dob", value: "dob", align: "left", width: "10"},
        {
            name: "gender",
            value: "gender",
            align: "left",
            width: "10",
            renderCell: (row) => (
                <SoftTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    sx={{display: "inline-block", width: "max-content"}}
                >
                    {row.gender?.replace("_", " ")}
                </SoftTypography>
            ),
        },
        {name: "branch", value: "branchName", align: "left", width: "10"},
        {
            name: "room",
            value: "roomName",
            align: "center",
            width: "10",
            cell: ""
        },
        {
            name: "branch status",
            value: "branchIsActive",
            align: "left",
            width: "10",
            cell: ""
        },
        {
            name: "status",
            value: "status",
            align: "center",
            width: "10",
            renderCell: (row) => renderPill(getStatusDisplayName(row.status), getStatusColor(row.status)),
        },
        {
            name: "Actions",
            value: "action",
            align: "center",
            width: "10",
            renderCell: (row) => (
                <>
                    <IconButton color="primary" size="small" title="View Details" onClick={() => handleViewDetails(row)}>
                        <VisibilityIcon/>
                    </IconButton>
                    <IconButton color="primary" size="small" title="Edit" onClick={() => handleEditChild(row)}>
                        <EditIcon/>
                    </IconButton>
                    <IconButton color="error" size="small" title="Delete" onClick={() => handleDeleteChild(row)}>
                        <DeleteIcon/>
                    </IconButton>
                </>
            ),
        },
    ];

    const loadChildren = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            console.log('Loading all children...');
            // Pass branchId and roomId to API for server-side filtering
            const response = await childApiClient.getAllChildren(
                selectedBranchId || null,
                selectedRoomId || null
            );
            console.log('Children response:', response);
            console.log('Children response type:', Array.isArray(response) ? 'Array' : typeof response);
            
            // Handle different response structures
            let childrenData = [];
            if (Array.isArray(response)) {
                childrenData = response;
                console.log('Using response directly as array');
            } else if (response && Array.isArray(response.data)) {
                childrenData = response.data;
                console.log('Using response.data as array');
            } else {
                console.warn('Unexpected children response structure:', response);
                childrenData = [];
            }
            
            // Log child statuses for debugging
            childrenData.forEach(child => {
                console.log(`Child: ${child.firstName} ${child.lastName} - Status: ${child.status}, Branch: ${child.branchName}, Branch Active: ${child.branchIsActive}`);
            });

            console.log('Children data to set:', childrenData);
            setChildren(childrenData);
            setFilteredChildren(childrenData);
        } catch (error) {
            console.error('Error loading children:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response,
                status: error.response?.status,
                data: error.response?.data
            });

            // Handle specific error cases
            if (error.response?.status === 403) {
                console.warn('Access forbidden for children endpoint - may need authentication or proper permissions');
                setError('Access denied. You may not have permission to view children data or need to log in.');
            } else {
                setError(`Failed to load children: ${error.message}`);
            }

            // Set empty arrays to prevent crashes
            setChildren([]);
            setFilteredChildren([]);
            setShowAlert(true);
        } finally {
            setIsLoading(false);
        }
    }, [selectedBranchId, selectedRoomId]);

    // Load branches on mount
    useEffect(() => {
        const loadBranches = async () => {
            try {
                setIsLoadingBranches(true);
                const response = await getAllBranches();
                setBranches(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error('Error loading branches:', error);
            } finally {
                setIsLoadingBranches(false);
            }
        };
        loadBranches();
    }, []);

    // Load rooms when branch selection changes
    useEffect(() => {
        if (selectedBranchId) {
            const loadRooms = async () => {
                try {
                    setIsLoadingRooms(true);
                    const response = await getAllRoomsByBranchId(selectedBranchId);
                    setRooms(Array.isArray(response.data) ? response.data : []);
                } catch (error) {
                    console.error('Error loading rooms:', error);
                    setRooms([]);
                } finally {
                    setIsLoadingRooms(false);
                }
            };
            loadRooms();
        } else {
            setRooms([]);
            setSelectedRoomId(null);
        }
    }, [selectedBranchId]);

    useEffect(() => {
        loadChildren().then();
    }, [loadChildren]);

    const handleFilterChildren = (value) => {
        let filtered = children.filter(child => {
            return child.firstName?.toLowerCase().includes(value.toLowerCase()) ||
                child.lastName?.toLowerCase().includes(value.toLowerCase()) ||
                child.gender?.toLowerCase().includes(value.toLowerCase()) ||
                child.branchName?.toLowerCase().includes(value.toLowerCase());
        });
        setFilteredChildren(filtered);
    }

    const handleAddChild = () => {
        setSelectedChild(null);
        setEditMode(false);
        setAddEditDialogOpen(true);
    };

    const handleEditChild = async (child) => {
        try {
            // Load complete child details including images
            const childDetailResponse = await childApiClient.getChildDetail(child.childId);
            console.log('Child detail for edit - raw response:', childDetailResponse);
            console.log('Child detail response type:', typeof childDetailResponse);
            console.log('Child detail response keys:', Object.keys(childDetailResponse || {}));

            let childDetailData = null;
            if (childDetailResponse && childDetailResponse.data) {
                childDetailData = childDetailResponse.data;
                console.log('Using childDetailResponse.data');
            } else if (childDetailResponse) {
                childDetailData = childDetailResponse;
                console.log('Using childDetailResponse directly');
            } else {
                throw new Error('No child detail data received');
            }

            console.log('Child detail data structure:', childDetailData);
            console.log('Child detail data keys:', Object.keys(childDetailData || {}));

            // Use the complete child data with images
            const completeChild = childDetailData.child || child;
            console.log('Complete child data for edit:', completeChild);
            console.log('Complete child images:', completeChild.images);

            // Use the complete child data for the form
            const childForEdit = {
                ...completeChild
            };
            console.log('Child data for edit:', childForEdit);

            setSelectedChild(childForEdit);
            setEditMode(true);
            setAddEditDialogOpen(true);
        } catch (error) {
            console.error('Error loading child details for edit:', error);
            setError('Failed to load child details for editing. Please try again.');
            setShowAlert(true);
        }
    };

    // Errors are not caught here: they propagate to AddChildrenDialog, which
    // shows the failure and keeps the dialog open. Catching them here made the
    // dialog's `await onSubmit` succeed, so a failed save also showed a success
    // toast.
    const handleOnSubmitChild = async (formData) => {
        // The dialog submits multipart FormData, not a plain object.
        const field = (name) => (typeof formData?.get === 'function' ? formData.get(name) : formData?.[name]) || '';

        if (editMode && selectedChild) {
            const childName = `${selectedChild.firstName || ''} ${selectedChild.lastName || ''}`.trim();
            await childApiClient.updateChild(selectedChild.childId, formData);
            setSuccess(`Child "${childName}" updated successfully.`);
        } else {
            const childName = `${field('firstName')} ${field('lastName')}`.trim();
            await childApiClient.addChild(formData);
            setSuccess(`Child "${childName}" added successfully.`);
        }
        setShowSuccess(true);
        await loadChildren();
        setAddEditDialogOpen(false);
        setSelectedChild(null);
        setEditMode(false);
    };

    const handleDeleteChild = (child) => {
        setChildToDelete(child);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDeleteChild = async () => {
        try {
            const childName = `${childToDelete?.firstName || ''} ${childToDelete?.lastName || ''}`.trim();
            await childApiClient.deleteChild(childToDelete.childId);
            await loadChildren();
            setDeleteDialogOpen(false);
            setChildToDelete(null);
            await loadChildren();
            setSuccess(`Child "${childName}" deleted successfully.`);
            setShowSuccess(true);
        } catch (error) {
            console.error('Error deleting child:', error);
            setError('Failed to delete child. Please try again.');
            setShowAlert(true);
        }
    };

    const handleCancelDeleteChild = () => {
        setDeleteDialogOpen(false);
        setChildToDelete(null);
    };

    const handleViewDetails = (child) => {
        navigate(`/children/${child.childId}`);
    };

    return (
        <DashboardLayout>
            <SoftBox pt={2} px={2} width="100%">
                {/* Page Title - Centered */}
                <SoftBox pt={2} px={2} display="flex" alignItems="center" justifyContent="center" width="100%" mb={2}>
                    <SoftTypography variant="h4" fontWeight="bold" color="text.primary">
                        Care Member Management
                    </SoftTypography>
                </SoftBox>
                
                {/* Filter Section */}
                <SoftBox pt={2} px={2} display="flex" alignItems="center" justifyContent="center" width="100%" gap={2}>
                    <SoftBox width="200px">
                        <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
                            Branch
                        </SoftTypography>
                        <Select
                            value={selectedBranchId || ''}
                            onChange={(e) => {
                                setSelectedBranchId(e.target.value);
                                setSelectedRoomId(null); // Reset room when branch changes
                            }}
                            displayEmpty
                            fullWidth
                            size="small"
                            disabled={isLoadingBranches}
                        >
                            <MenuItem value="">
                                <em>All Branches</em>
                            </MenuItem>
                            {isLoadingBranches ? (
                                <MenuItem disabled>
                                    <CircularProgress size={20} sx={{mr: 1}}/>
                                    Loading...
                                </MenuItem>
                            ) : branches.length === 0 ? (
                                <MenuItem disabled>No branches available</MenuItem>
                            ) : (
                                branches.map((branch) => (
                                    <MenuItem key={branch.branchId} value={branch.branchId}>
                                        {branch.location || branch.name}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </SoftBox>
                    
                    <SoftBox width="200px">
                        <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
                            Room
                        </SoftTypography>
                        <Select
                            value={selectedRoomId || ''}
                            onChange={(e) => setSelectedRoomId(e.target.value)}
                            displayEmpty
                            fullWidth
                            size="small"
                            disabled={isLoadingRooms}
                        >
                            <MenuItem value="">
                                <em>All Rooms</em>
                            </MenuItem>
                            {isLoadingRooms ? (
                                <MenuItem disabled>
                                    <CircularProgress size={20} sx={{mr: 1}}/>
                                    Loading...
                                </MenuItem>
                            ) : rooms.length === 0 ? (
                                <MenuItem disabled>No rooms available</MenuItem>
                            ) : (
                                rooms.map((room) => (
                                    <MenuItem key={room.roomId} value={room.roomId}>
                                        {room.name}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </SoftBox>
                    
                    {(selectedBranchId || selectedRoomId) && (
                        <SoftButton 
                            size="small" 
                            color="primary"
                            onClick={() => {
                                setSelectedBranchId(null);
                                setSelectedRoomId(null);
                            }}
                        >
                            Clear Filters
                        </SoftButton>
                    )}
                </SoftBox>
                
                <SoftBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <SoftBox display="flex" alignItems="center" gap={2}>
                        {isOnChildRoute && (
                            <IconButton
                                onClick={() => navigate('/children')}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    }
                                }}
                            >
                                <ArrowBackIcon/>
                            </IconButton>
                        )}
                    </SoftBox>
                    {!isOnChildRoute && (
                        <>
                            <SoftBox flexGrow={1} maxWidth={560}>
                                <SoftInput
                                    placeholder="Type here to filter by name, gender or branch"
                                    icon={{component: "search", direction: "left"}}
                                    onChange={(event) => handleFilterChildren(event.target.value)}
                                />
                            </SoftBox>
                            <SoftButton color="primary" onClick={handleAddChild} sx={{flexShrink: 0}}>
                                Add Child
                            </SoftButton>
                        </>
                    )}
                </SoftBox>
            </SoftBox>
            <SoftBox p={2} width="100%">
                {isOnChildRoute ? (
                    <Outlet/>
                ) : (
                    <EnhancedTable
                        TableComponent={DataTable}
                        isLoading={isLoading}
                        data={filteredChildren}
                        loadingMessage="Loading children..."
                        noDataMessage="No children found"
                        columns={columns}
                    />
                )}
            </SoftBox>
            {error && (
                <SoftAlert
                    color="error"
                    dismissible={false}
                    timeout={10}
                    mount={showAlert}
                    onClose={() => {
                        setError(null);
                        setShowAlert(false);
                    }}
                >
                    {error}
                </SoftAlert>
            )}
            {success && (
                <SoftAlert
                    color="success"
                    dismissible={false}
                    timeout={10}
                    mount={showSuccess}
                    onClose={() => {
                        setSuccess(null);
                        setShowSuccess(false);
                    }}
                >
                    {success}
                </SoftAlert>
            )}
            <AddChildrenDialog
                open={addEditDialogOpen}
                onClose={() => setAddEditDialogOpen(false)}
                child={selectedChild}
                isEdit={editMode}
                onSubmit={handleOnSubmitChild}
                guardian={null} // This will be handled in the dialog
            />
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={handleCancelDeleteChild}
                onDelete={handleConfirmDeleteChild}
                warningMessage={`Are you sure you want to delete ${childToDelete?.firstName || ""} ${childToDelete?.lastName || ""}? This action cannot be undone.`}
            />
        </DashboardLayout>
    );
}

export default Overview;
